import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

// Local cart shape (logged-out): [{ id, variant_id?, name, price, image, brand, qty, ... }]
// Cart lines are keyed on (id, variant_id) — two variants of the same product coexist as
// separate lines. `line_id` is a composite string used by React keys and remove/update calls.
const LOCAL_KEY = 'bq_cart';

const readLocal = () => {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]'); } catch { return []; }
};
const writeLocal = (items) => localStorage.setItem(LOCAL_KEY, JSON.stringify(items));

const lineKey = (productId, variantId) => `${productId}::${variantId || ''}`;

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);

  const setFromServer = (serverCart) => {
    const mapped = serverCart.items.map(it => ({
      id: it.product.id,
      variant_id: it.product.variant_id || null,
      line_id: lineKey(it.product.id, it.product.variant_id),
      name: it.product.name,
      price: it.product.price,
      image: it.product.image,
      brand: it.product.brand,
      qty: it.qty,
      collect: !!it.collect,
      coverage_per_box_sqm: it.product.coverage_per_box_sqm || null,
      individual_delivery_cost: it.product.individual_delivery_cost || null,
      free_delivery_qty: it.product.free_delivery_qty || null,
      shipping_slabs: it.product.shipping_slabs || [],
      collection_price_end_user: it.product.collection_price_end_user || null,
      collection_price_builder: it.product.collection_price_builder || null,
      collection_price_retailer: it.product.collection_price_retailer || null,
    }));
    setItems(mapped);
  };

  const refresh = async () => {
    if (!user) { setItems(readLocal()); return; }
    try {
      const res = await api.get('/cart');
      setFromServer(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      const local = readLocal();
      const mergeAndLoad = async () => {
        try {
          if (local.length > 0) {
            const res = await api.post('/cart/merge', {
              items: local.map(i => ({ product_id: i.id, qty: i.qty, variant_id: i.variant_id || undefined }))
            });
            setFromServer(res.data);
            writeLocal([]);
          } else {
            const res = await api.get('/cart');
            setFromServer(res.data);
          }
        } catch (e) {
          setItems(local);
        }
      };
      mergeAndLoad();
    } else {
      setItems(readLocal());
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (!user) writeLocal(items);
  }, [items, user]);

  const addItem = useCallback(async (product, qty = 1, variantId = null, extras = {}) => {
    const vid = variantId || product.variant_id || null;
    const collectFlag = extras && extras.collect != null ? !!extras.collect : null;
    if (user) {
      try {
        const payload = { product_id: product.id, qty };
        if (vid) payload.variant_id = vid;
        if (collectFlag != null) payload.collect = collectFlag;
        const res = await api.post('/cart/add', payload);
        setFromServer(res.data);
        return;
      } catch (e) { /* fall back to local */ }
    }
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === product.id && (i.variant_id || null) === (vid || null));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = {
          ...copy[idx],
          qty: copy[idx].qty + qty,
          ...(collectFlag != null ? { collect: collectFlag } : {}),
        };
        return copy;
      }
      return [...prev, {
        id: product.id, variant_id: vid, line_id: lineKey(product.id, vid),
        name: product.name, price: product.price, image: product.image, brand: product.brand, qty,
        collect: !!collectFlag,
        coverage_per_box_sqm: product.coverage_per_box_sqm || null,
        individual_delivery_cost: product.individual_delivery_cost || null,
        free_delivery_qty: product.free_delivery_qty || null,
        shipping_slabs: product.shipping_slabs || [],
        collection_price_end_user: product.collection_price_end_user || null,
        collection_price_builder: product.collection_price_builder || null,
        collection_price_retailer: product.collection_price_retailer || null,
      }];
    });
  }, [user]);

  const updateQty = useCallback(async (id, qty, variantId = null) => {
    if (user) {
      try {
        const payload = { product_id: id, qty };
        if (variantId) payload.variant_id = variantId;
        const res = await api.put('/cart/update', payload);
        setFromServer(res.data);
        return;
      } catch (e) { /* fall back */ }
    }
    if (qty < 1) {
      setItems(prev => prev.filter(i => !(i.id === id && (i.variant_id || null) === (variantId || null))));
    } else {
      setItems(prev => prev.map(i =>
        i.id === id && (i.variant_id || null) === (variantId || null) ? { ...i, qty } : i
      ));
    }
  }, [user]);

  const setCollect = useCallback(async (id, collect, variantId = null) => {
    if (user) {
      try {
        const line = items.find(i => i.id === id && (i.variant_id || null) === (variantId || null));
        if (!line) return;
        const payload = { product_id: id, qty: line.qty, collect: !!collect };
        if (variantId) payload.variant_id = variantId;
        const res = await api.put('/cart/update', payload);
        setFromServer(res.data);
        return;
      } catch (e) { /* fall back */ }
    }
    setItems(prev => prev.map(i =>
      i.id === id && (i.variant_id || null) === (variantId || null) ? { ...i, collect: !!collect } : i
    ));
  }, [user, items]);

  const removeItem = useCallback(async (id, variantId = null) => {
    if (user) {
      try {
        const url = variantId ? `/cart/${id}?variant_id=${encodeURIComponent(variantId)}` : `/cart/${id}`;
        const res = await api.delete(url);
        setFromServer(res.data);
        return;
      } catch (e) { /* fall back */ }
    }
    setItems(prev => prev.filter(i => !(i.id === id && (i.variant_id || null) === (variantId || null))));
  }, [user]);

  const clearCart = useCallback(async () => {
    if (user) {
      try {
        const res = await api.delete('/cart');
        setFromServer(res.data);
        return;
      } catch (e) { /* fall back */ }
    }
    setItems([]);
  }, [user]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clearCart, setCollect, count, subtotal, refresh }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
